import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import { formatInBusinessTimezone } from '../utils/dateTimeHelper';
import {
  getHostedVideoPlaybackUrl,
  getHostedVideoThumbnailUrl,
} from '../utils/hostedVideoPlaybackUrl';

const VideoScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    loadData(true);
  }, []);

  const itemsWithAlbum = useMemo(() => {
    const normalized = items.map((item, index) => {
      const uploadedAtMs = item.creationDate ? new Date(item.creationDate).getTime() : 0;
      return {
        ...item,
        id: item.id ?? `${index}_${item.title}`,
        album: (item.category && String(item.category).trim()) || 'General',
        uploadedAtMs: Number.isFinite(uploadedAtMs) ? uploadedAtMs : 0,
      };
    });

    return normalized.sort((a, b) => b.uploadedAtMs - a.uploadedAtMs);
  }, [items]);

  const groupedCategories = useMemo(() => {
    const grouped = itemsWithAlbum.reduce((acc, item) => {
      const name = item.album;
      if (!acc[name]) acc[name] = [];
      acc[name].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([category, videos]) => ({
      category,
      videos,
    }));
  }, [itemsWithAlbum]);

  const selectedCategoryVideos = useMemo(() => {
    if (!selectedCategory) return [];
    const g = groupedCategories.find((entry) => entry.category === selectedCategory);
    return g ? g.videos : [];
  }, [groupedCategories, selectedCategory]);

  const loadData = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setPageNumber(1);
        setHasMoreData(true);
      }
      const pageSize = 10;
      const currentPage = isInitial ? 1 : pageNumber;
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `/api/school-common/video/get-video-student?PageSize=${pageSize}&PageNumber=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const newData = response.data.data || [];
      if (isInitial) {
        setItems(newData);
      } else {
        setItems((prev) => [...prev, ...newData]);
      }
      if (newData.length === pageSize) {
        setPageNumber(currentPage + 1);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      handleCatch(error);
      if (isInitial) setItems([]);
      setNotification({ open: true, message: 'Failed to load videos', severity: 'error' });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const onRefresh = async () => {
    await loadData(true);
  };

  const loadMoreData = async () => {
    if (hasMoreData && !loading) {
      setLoading(true);
      await loadData(false);
    }
  };

  const handlePlayVideo = (videoUrl) => {
    try {
      const fullUrl = getHostedVideoPlaybackUrl(videoUrl);
      if (!fullUrl) {
        setNotification({
          open: true,
          message: 'Cannot open this video link',
          severity: 'error',
        });
        return;
      }
      window.open(fullUrl, '_blank');
    } catch (error) {
      console.error('Error opening video:', error);
      setNotification({ open: true, message: 'Failed to open video', severity: 'error' });
    }
  };

  const renderVideoCard = (item, { showCategoryChip = false } = {}) => {
    const thumbnailUrl = getHostedVideoThumbnailUrl(item.videoUrl);

    return (
      <Box
      key={item.id}
      sx={{
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        mx: 0.5,
        mt: 1,
        overflow: 'hidden',
      }}
    >
        <Box
        onClick={() => handlePlayVideo(item.videoUrl)}
        sx={{
          position: 'relative',
          paddingTop: '56.25%',
          bgcolor: '#1a1a2e',
          cursor: 'pointer',
        }}
      >
        {thumbnailUrl && (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={item.title || 'Video thumbnail'}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 34, color: colors.BLUE, ml: 0.5 }} />
        </Box>
        {showCategoryChip && item.category && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: 10,
              px: 1,
              py: 0.3,
              borderRadius: 1,
            }}
          >
            {item.category}
          </Box>
        )}
        </Box>

        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography
          sx={{
            fontWeight: 'bold',
            fontSize: 14,
            color: '#222',
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {item.title}
        </Typography>
        {item.description && (
          <Typography
            sx={{
              fontSize: 12,
              color: '#666',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {item.createdBy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <PersonIcon sx={{ fontSize: 12, color: '#888' }} />
              <Typography sx={{ fontSize: 11, color: '#888' }}>{item.createdBy}</Typography>
            </Box>
          )}
          {item.creationDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <CalendarTodayIcon sx={{ fontSize: 12, color: '#888' }} />
              <Typography sx={{ fontSize: 11, color: '#888' }}>
                {formatInBusinessTimezone(item.creationDate, 'MMM dd, yyyy')}
              </Typography>
            </Box>
          )}
        </Box>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<PlayArrowIcon />}
          onClick={() => handlePlayVideo(item.videoUrl)}
          sx={{
            bgcolor: colors.BLUE,
            height: 42,
            borderRadius: 21,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: 13,
            '&:hover': { bgcolor: '#0052A3' },
          }}
        >
          Play Video
        </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="Videos"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>Loading videos...</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 64, mb: 1.5 }}></Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
              No Videos Available
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              Educational videos will appear here. Check back later!
            </Typography>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Box>
        ) : !selectedCategory ? (
          <>
            <Typography sx={{ px: 1, py: 1, fontSize: 14, color: 'text.secondary' }}>
              Select a category
            </Typography>
            {groupedCategories.map((group) => (
              <Box
                key={group.category}
                onClick={() => setSelectedCategory(group.category)}
                sx={{
                  mx: 1,
                  mb: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: '#fff',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ flex: 1, pr: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    {group.category}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    {group.videos.length} videos
                  </Typography>
                </Box>
                <Box sx={{ width: 56, height: 56, position: 'relative' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gridTemplateRows: 'repeat(2, 1fr)',
                      gap: '2px',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#E5E7EB',
                    }}
                  >
                    {[0, 1, 2, 3].map((idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: '100%',
                          height: '100%',
                          bgcolor: '#1a1a2e',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}
            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreData}
                  disabled={loading}
                  sx={{ borderRadius: 20, px: 4 }}
                >
                  {loading ? <CircularProgress size={22} /> : 'Load More Categories'}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{ px: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setSelectedCategory(null)}>
                Back
              </Button>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
                {selectedCategory}
              </Typography>
            </Box>
            {selectedCategoryVideos.map((item) =>
              renderVideoCard(item, { showCategoryChip: true })
            )}
            {selectedCategoryVideos.length === 0 && (
              <Typography sx={{ px: 2, py: 2, fontSize: 14, color: 'text.secondary' }}>
                No videos in this category.
              </Typography>
            )}
            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreData}
                  disabled={loading}
                  sx={{ borderRadius: 20, px: 4 }}
                >
                  {loading ? <CircularProgress size={22} /> : 'Load More Videos'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideoScreen;
